import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { getWorkoutPlan, getPlanProgression, addProgression, updatePlanStatus, deleteWorkoutPlan } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import DashboardLayout from '../components/DashboardLayout';
import { Loader2, ArrowLeft, Calendar, Plus, Star, CheckCircle2, Play, Dumbbell, Heart, Home, Building2, Download, Share2, Trophy, RotateCcw, Trash2, CloudOff } from 'lucide-react';
import { toast } from 'sonner';
import { queueProgression } from '../lib/offlineQueue';
import { useLanguage } from '../context/LanguageContext';

const WORKOUT_TYPES = {
  cardio: { name: 'Cardio', icon: Heart, color: 'text-red-400' },
  strength: { name: 'Musculação', icon: Dumbbell, color: 'text-blue-400' },
  full_body: { name: 'Corpo Inteiro', icon: Dumbbell, color: 'text-[#32CD32]' },
  upper_body: { name: 'Superior', icon: Dumbbell, color: 'text-purple-400' },
  lower_body: { name: 'Inferior', icon: Dumbbell, color: 'text-orange-400' },
  hiit: { name: 'HIIT', icon: Heart, color: 'text-yellow-400' },
  circuit: { name: 'Circuito', icon: Dumbbell, color: 'text-cyan-400' }
};

const LOCATIONS = {
  home: { name: 'Casa', icon: Home },
  gym: { name: 'Ginásio', icon: Building2 },
  both: { name: 'Ambos', icon: Dumbbell }
};

const PlanDetailPage = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const [progressions, setProgressions] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [progressionForm, setProgressionForm] = useState({
    week_number: '',
    completed_sessions: '',
    performance_rating: '',
    notes: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [planData, progressData] = await Promise.all([
          getWorkoutPlan(planId),
          getPlanProgression(planId)
        ]);
        setPlan(planData);
        setProgressions(progressData);
      } catch (error) {
        console.error('Error fetching plan:', error);
        toast.error('Erro ao carregar plano');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [planId]);

  // Refresh entries once the offline queue finishes syncing
  useEffect(() => {
    const onSyncDone = async () => {
      try {
        setProgressions(await getPlanProgression(planId));
      } catch (error) {
        console.error('Error refreshing progression:', error);
      }
    };
    window.addEventListener('pb-sync-done', onSyncDone);
    return () => window.removeEventListener('pb-sync-done', onSyncDone);
  }, [planId]);

  const handleAddProgression = async (e) => {
    e.preventDefault();
    if (!progressionForm.week_number || !progressionForm.completed_sessions || !progressionForm.performance_rating) {
      toast.error('Preenche todos os campos obrigatórios');
      return;
    }

    setSubmitting(true);
    const entry = {
      workout_plan_id: planId,
      week_number: parseInt(progressionForm.week_number),
      completed_sessions: parseInt(progressionForm.completed_sessions),
      performance_rating: parseInt(progressionForm.performance_rating),
      notes: progressionForm.notes || null
    };

    const resetForm = () => {
      setProgressionForm({ week_number: '', completed_sessions: '', performance_rating: '', notes: '' });
      setDialogOpen(false);
    };

    try {
      const result = await addProgression(entry);

      setProgressions([result, ...progressions]);
      resetForm();
      toast.success('Progresso registado!');
    } catch (error) {
      // No network: keep the entry locally and sync it when connection is back
      if (!navigator.onLine || !error.response) {
        const queued = queueProgression(entry);
        setProgressions([{ ...queued, created_at: queued.queued_at, pending_sync: true }, ...progressions]);
        resetForm();
        toast.info(t('offlineSavedLocally'));
      } else {
        console.error('Error adding progression:', error);
        toast.error('Erro ao registar progresso');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const openVideoDialog = (exercise) => {
    setSelectedVideo(exercise);
    setVideoDialogOpen(true);
  };

  const handleToggleStatus = async () => {
    setUpdatingStatus(true);
    try {
      const currentStatus = plan.status || 'active';
      const newStatus = currentStatus === 'completed' ? 'active' : 'completed';
      const result = await updatePlanStatus(planId, newStatus);
      
      setPlan({
        ...plan,
        status: newStatus,
        completed_at: result.completed_at
      });
      
      if (newStatus === 'completed') {
        toast.success('🏆 Parabéns! Plano marcado como concluído!');
      } else {
        toast.success('Plano reativado!');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erro ao atualizar status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteWorkoutPlan(planId);
      toast.success('Plano eliminado com sucesso!');
      navigate('/history');
    } catch (error) {
      console.error('Error deleting plan:', error);
      toast.error('Erro ao eliminar plano');
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#32CD32]" />
        </div>
      </DashboardLayout>
    );
  }

  if (!plan) {
    return (
      <DashboardLayout>
        <div className="text-center py-16">
          <p className="text-gray-500">Plano não encontrado</p>
          <Link to="/history">
            <Button variant="ghost" className="mt-4 text-[#32CD32]">
              <ArrowLeft className="mr-2 w-4 h-4" />
              Voltar ao histórico
            </Button>
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  const workoutType = WORKOUT_TYPES[plan.workout_type] || WORKOUT_TYPES.full_body;
  const TypeIcon = workoutType.icon;
  const location = LOCATIONS[plan.training_location] || LOCATIONS.both;
  const LocationIcon = location.icon;
  const isCompleted = plan.status === 'completed';

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const token = localStorage.getItem('cardioai_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workout-plans/${planId}/pdf`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Erro ao gerar PDF');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `plano_treino_${planId.substring(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('PDF exportado com sucesso!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Erro ao exportar PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const handleShare = async () => {
    const shareText = `🏋️ Acabei de criar o meu plano de treino de ${plan.duration_weeks} semanas com Process Body AI! #fitness #treino #ProcessBodyAI`;
    const shareUrl = window.location.href;
    
    // Try native share first (mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Plano de Treino - Process Body AI`,
          text: shareText,
          url: shareUrl
        });
        toast.success('Partilhado com sucesso!');
      } catch (err) {
        if (err.name !== 'AbortError') {
          // Fallback to clipboard
          copyToClipboard(shareText + '\n' + shareUrl);
        }
      }
    } else {
      // Desktop fallback - show share options
      const options = [
        { name: 'Twitter/X', url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}` },
        { name: 'Facebook', url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}` },
        { name: 'WhatsApp', url: `https://wa.me/?text=${encodeURIComponent(shareText + '\n' + shareUrl)}` },
        { name: 'LinkedIn', url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}` }
      ];
      
      // Open Twitter by default, or copy to clipboard
      window.open(options[0].url, '_blank', 'width=600,height=400');
      toast.success('A abrir partilha...');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Copiado para a área de transferência!');
    }).catch(() => {
      toast.error('Erro ao copiar');
    });
  };

  return (
    <DashboardLayout>
      <div data-testid="plan-detail-page">
        {/* Completed Banner */}
        {isCompleted && (
          <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-green-500/20 to-green-600/10 border border-green-500/30 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
              <Trophy className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <h3 className="font-bold text-green-400 uppercase" style={{ fontFamily: 'Barlow Condensed' }}>
                Plano Concluído!
              </h3>
              <p className="text-gray-400 text-sm">
                Parabéns! Concluíste este plano em {plan.completed_at ? formatDate(plan.completed_at) : 'data desconhecida'}
              </p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <Link to="/history" className="inline-flex items-center text-gray-400 hover:text-[#32CD32] mb-4 transition-colors">
            <ArrowLeft className="mr-2 w-4 h-4" />
            Voltar ao histórico
          </Link>
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 
                  className="text-3xl md:text-4xl font-bold tracking-tight uppercase"
                  style={{ fontFamily: 'Barlow Condensed' }}
                >
                  Plano de <span className="text-[#32CD32]">{plan.duration_weeks} Semanas</span>
                </h1>
                {isCompleted && (
                  <span className="px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-sm font-medium">
                    Concluído
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-4 text-gray-400">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatDate(plan.created_at)}
                </span>
                <span className={`flex items-center gap-1 ${workoutType.color}`}>
                  <TypeIcon className="w-4 h-4" />
                  {workoutType.name}
                </span>
                <span className="flex items-center gap-1">
                  <LocationIcon className="w-4 h-4" />
                  {location.name}
                </span>
                {plan.focus_area && (
                  <span className="text-[#32CD32]">• {plan.focus_area}</span>
                )}
              </div>
            </div>
            
            <div className="flex flex-wrap gap-3">
              {/* Mark as Completed / Reactivate Button */}
              <Button 
                variant="outline" 
                className={`px-4 py-3 rounded-full transition-colors ${
                  isCompleted 
                    ? 'border-blue-500/50 text-blue-400 hover:bg-blue-500/10' 
                    : 'border-green-500/50 text-green-400 hover:bg-green-500/10'
                }`}
                onClick={handleToggleStatus}
                disabled={updatingStatus}
                data-testid="toggle-status-btn"
              >
                {updatingStatus ? (
                  <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                ) : isCompleted ? (
                  <RotateCcw className="mr-2 w-4 h-4" />
                ) : (
                  <CheckCircle2 className="mr-2 w-4 h-4" />
                )}
                {isCompleted ? 'Reativar' : 'Marcar Concluído'}
              </Button>

              <Button 
                variant="outline" 
                className="px-4 py-3 rounded-full border-white/20 hover:bg-white/5"
                onClick={handleExportPdf}
                disabled={exportingPdf}
                data-testid="export-pdf-btn"
              >
                {exportingPdf ? (
                  <>
                    <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                    A exportar...
                  </>
                ) : (
                  <>
                    <Download className="mr-2 w-4 h-4" />
                    Exportar PDF
                  </>
                )}
              </Button>
              
              <Button 
                variant="outline" 
                className="px-4 py-3 rounded-full border-white/20 hover:bg-white/5"
                onClick={handleShare}
                data-testid="share-btn"
              >
                <Share2 className="mr-2 w-4 h-4" />
                Partilhar
              </Button>
              
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary px-6 py-3 rounded-full" data-testid="add-progress-btn">
                    <Plus className="mr-2 w-4 h-4" />
                    Registar Progresso
                  </Button>
                </DialogTrigger>
              <DialogContent className="bg-[#121212] border-white/10 max-w-md">
                <DialogHeader>
                  <DialogTitle 
                    className="text-xl uppercase tracking-tight"
                    style={{ fontFamily: 'Barlow Condensed' }}
                  >
                    Registar Progresso
                  </DialogTitle>
                  <DialogDescription>
                    Acompanha a tua evolução neste plano
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleAddProgression} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-300">Semana *</Label>
                      <Select 
                        value={progressionForm.week_number} 
                        onValueChange={(value) => setProgressionForm({ ...progressionForm, week_number: value })}
                      >
                        <SelectTrigger className="h-12 bg-black/50 border-white/20 rounded-lg" data-testid="week-select">
                          <SelectValue placeholder="Semana" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#121212] border-white/20">
                          {Array.from({ length: plan.duration_weeks }, (_, i) => (
                            <SelectItem key={i + 1} value={(i + 1).toString()}>
                              Semana {i + 1}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-300">Sessões Completas *</Label>
                      <Input
                        type="number"
                        min="0"
                        max="7"
                        placeholder="0"
                        value={progressionForm.completed_sessions}
                        onChange={(e) => setProgressionForm({ ...progressionForm, completed_sessions: e.target.value })}
                        className="h-12 bg-black/50 border-white/20 rounded-lg"
                        data-testid="sessions-input"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-gray-300">Avaliação de Desempenho *</Label>
                    <Select 
                      value={progressionForm.performance_rating} 
                      onValueChange={(value) => setProgressionForm({ ...progressionForm, performance_rating: value })}
                    >
                      <SelectTrigger className="h-12 bg-black/50 border-white/20 rounded-lg" data-testid="rating-select">
                        <SelectValue placeholder="Avalia o teu desempenho" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-white/20">
                        <SelectItem value="1">1 - Muito difícil</SelectItem>
                        <SelectItem value="2">2 - Difícil</SelectItem>
                        <SelectItem value="3">3 - Moderado</SelectItem>
                        <SelectItem value="4">4 - Confortável</SelectItem>
                        <SelectItem value="5">5 - Fácil</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-300">Notas (opcional)</Label>
                    <Textarea
                      placeholder="Como te sentiste? Alguma observação?"
                      value={progressionForm.notes}
                      onChange={(e) => setProgressionForm({ ...progressionForm, notes: e.target.value })}
                      className="min-h-[80px] bg-black/50 border-white/20 rounded-lg resize-none"
                      data-testid="notes-input"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full btn-primary h-12 rounded-xl"
                    disabled={submitting}
                    data-testid="save-progress-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                        A guardar...
                      </>
                    ) : (
                      'Guardar Progresso'
                    )}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Progression */}
            <Card className="bg-[#121212] border-white/10">
              <CardHeader>
                <CardTitle 
                  className="text-lg uppercase tracking-tight"
                  style={{ fontFamily: 'Barlow Condensed' }}
                >
                  Progresso
                </CardTitle>
                <CardDescription>O teu acompanhamento semanal</CardDescription>
              </CardHeader>
              <CardContent>
                {progressions.length === 0 ? (
                  <p className="text-[#A1A1AA] text-sm text-center py-4">
                    Nenhum progresso registado ainda
                  </p>
                ) : (
                  <div className="space-y-3">
                    {progressions.map((prog) => (
                      <div 
                        key={prog.id || prog.queued_id} 
                        className="p-3 rounded-lg bg-black/30 border border-white/5"
                        data-testid={`progression-${prog.id || prog.queued_id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-[#32CD32]">Semana {prog.week_number}</span>
                          <div className="flex items-center gap-1">
                            {prog.pending_sync && (
                              <span className="flex items-center gap-1 text-[10px] text-amber-400 mr-1" data-testid="progression-pending-badge">
                                <CloudOff className="w-3 h-3" />
                                {t('offlinePendingBadge')}
                              </span>
                            )}
                            {Array.from({ length: 5 }).map((_, i) => (
                              <Star
                                key={i}
                                className={`w-3 h-3 ${i < prog.performance_rating ? 'text-[#32CD32] fill-[#32CD32]' : 'text-gray-600'}`}
                              />
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                          <CheckCircle2 className="w-4 h-4" />
                          {prog.completed_sessions} sessões
                        </div>
                        {prog.notes && (
                          <p className="text-xs text-gray-500 mt-2">{prog.notes}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Exercise Videos */}
            {plan.exercises && plan.exercises.length > 0 && (
              <Card className="bg-[#121212] border-white/10">
                <CardHeader>
                  <CardTitle 
                    className="text-lg uppercase tracking-tight flex items-center gap-2"
                    style={{ fontFamily: 'Barlow Condensed' }}
                  >
                    <Play className="w-5 h-5 text-[#32CD32]" />
                    Vídeos de Demonstração
                  </CardTitle>
                  <CardDescription>Exercícios do plano</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {plan.exercises.map((exercise, index) => (
                      <button
                        key={index}
                        onClick={() => openVideoDialog(exercise)}
                        className="w-full p-3 rounded-lg bg-black/30 border border-white/5 hover:border-[#32CD32]/50 transition-colors text-left flex items-center gap-3"
                        data-testid={`exercise-video-${exercise.id}`}
                      >
                        <div className="w-10 h-10 rounded-full bg-[#32CD32]/10 flex items-center justify-center flex-shrink-0">
                          <Play className="w-4 h-4 text-[#32CD32]" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{exercise.name}</p>
                          <p className="text-xs text-gray-500 capitalize">{exercise.category}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Plan Content */}
          <Card className="lg:col-span-2 bg-[#121212] border-white/10">
            <CardHeader>
              <CardTitle 
                className="text-xl uppercase tracking-tight"
                style={{ fontFamily: 'Barlow Condensed' }}
              >
                Plano de Treino
              </CardTitle>
              <CardDescription>Gerado por IA especialmente para ti</CardDescription>
            </CardHeader>
            <CardContent>
              <div 
                className="workout-content prose prose-invert max-w-none prose-headings:text-white prose-h1:text-2xl prose-h1:font-bold prose-h1:uppercase prose-h2:text-xl prose-h2:text-[#32CD32] prose-h3:text-lg prose-strong:text-white prose-a:text-[#32CD32] prose-li:text-gray-200 prose-p:text-gray-200"
                data-testid="plan-content"
              >
                <ReactMarkdown
                  components={{
                    a: ({ href, children }) => {
                      // Check if it's a YouTube link or "Ver Exercício" link
                      if (href?.includes('youtube.com')) {
                        const watchUrl = href.includes('/embed/') 
                          ? href.replace('/embed/', '/watch?v=') 
                          : href;
                        return (
                          <a 
                            href={watchUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#32CD32]/15 border border-[#32CD32]/40 text-[#32CD32] hover:bg-[#32CD32]/25 transition-colors text-sm font-medium mt-2"
                          >
                            <Play className="w-4 h-4" />
                            {children || 'Ver Exercício'}
                          </a>
                        );
                      }
                      return (
                        <a 
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#32CD32] hover:underline"
                        >
                          {children}
                        </a>
                      );
                    },
                    h2: ({ children }) => {
                      // Check if it's an exercise header (starts with "Exercício")
                      const text = String(children);
                      if (text.includes('Exercício')) {
                        const parts = text.split(':');
                        if (parts.length > 1) {
                          return (
                            <h2 className="text-lg font-bold text-white mt-6 mb-2 flex items-baseline gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                              <span className="text-[#32CD32]">{parts[0]}:</span>
                              <span className="text-xl">{parts[1]}</span>
                            </h2>
                          );
                        }
                      }
                      return (
                        <h2 className="text-xl font-bold uppercase text-[#32CD32] mt-6 mb-3" style={{ fontFamily: 'Barlow Condensed' }}>
                          {children}
                        </h2>
                      );
                    },
                    h3: ({ children }) => {
                      // Check if it's an exercise header (starts with "Exercício")
                      const text = String(children);
                      if (text.includes('Exercício')) {
                        const parts = text.split(':');
                        if (parts.length > 1) {
                          return (
                            <h3 className="text-base font-bold text-white mt-4 mb-2 flex items-baseline gap-2">
                              <span className="text-[#32CD32]">{parts[0]}:</span>
                              <span className="text-lg">{parts[1]}</span>
                            </h3>
                          );
                        }
                      }
                      return (
                        <h3 className="text-lg font-semibold text-white mt-4 mb-2">
                          {children}
                        </h3>
                      );
                    },
                    h1: ({ children }) => (
                      <h1 className="text-2xl font-bold uppercase text-white mt-6 mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
                        {children}
                      </h1>
                    ),
                    strong: ({ children }) => (
                      <strong className="font-bold text-white">{children}</strong>
                    ),
                    p: ({ children }) => (
                      <p className="text-gray-200 mb-2">{children}</p>
                    ),
                    ul: ({ children }) => (
                      <ul className="list-disc list-inside space-y-1 mb-4">{children}</ul>
                    ),
                    ol: ({ children }) => (
                      <ol className="list-decimal list-inside space-y-1 mb-4">{children}</ol>
                    ),
                    li: ({ children }) => (
                      <li className="text-gray-200">{children}</li>
                    ),
                    hr: () => (
                      <hr className="border-white/10 my-6" />
                    ),
                    table: ({ children }) => (
                      <div className="overflow-x-auto my-4">
                        <table className="w-full border-collapse">{children}</table>
                      </div>
                    ),
                    thead: ({ children }) => (
                      <thead className="bg-[#32CD32]/10">{children}</thead>
                    ),
                    th: ({ children }) => (
                      <th className="border border-white/10 px-3 py-2 text-left text-white font-semibold">{children}</th>
                    ),
                    td: ({ children }) => (
                      <td className="border border-white/10 px-3 py-2 text-gray-200">{children}</td>
                    ),
                  }}
                >
                  {plan.plan_content}
                </ReactMarkdown>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Video Dialog */}
        <Dialog open={videoDialogOpen} onOpenChange={setVideoDialogOpen}>
          <DialogContent className="bg-[#121212] border-white/10 max-w-3xl">
            <DialogHeader>
              <DialogTitle 
                className="text-xl uppercase tracking-tight"
                style={{ fontFamily: 'Barlow Condensed' }}
              >
                {selectedVideo?.name}
              </DialogTitle>
              <DialogDescription className="capitalize">
                {selectedVideo?.category}
              </DialogDescription>
            </DialogHeader>
            {selectedVideo && (
              <div className="aspect-video w-full mt-4">
                <iframe
                  src={selectedVideo.video_url}
                  title={selectedVideo.name}
                  className="w-full h-full rounded-lg"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Plan Section */}
        <Card className="mt-8 bg-[#121212] border-red-500/20">
          <CardContent className="py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="font-bold text-red-400 uppercase" style={{ fontFamily: 'Barlow Condensed' }}>
                  Zona de Perigo
                </h3>
                <p className="text-gray-400 text-sm">
                  Eliminar este plano permanentemente. Esta ação não pode ser revertida.
                </p>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="px-6 py-3 rounded-full border-red-500/50 text-red-400 hover:bg-red-500/10"
                    data-testid="delete-plan-btn"
                  >
                    <Trash2 className="mr-2 w-4 h-4" />
                    Eliminar Plano
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-[#121212] border-white/10">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-white">Tens a certeza?</AlertDialogTitle>
                    <AlertDialogDescription className="text-gray-400">
                      Esta ação não pode ser revertida. O plano e todo o progresso associado serão eliminados permanentemente.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-transparent border-white/20 text-white hover:bg-white/10">
                      Cancelar
                    </AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={handleDelete}
                      disabled={deleting}
                      className="bg-red-600 text-white hover:bg-red-700"
                    >
                      {deleting ? (
                        <>
                          <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                          A eliminar...
                        </>
                      ) : (
                        'Eliminar'
                      )}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default PlanDetailPage;
